import styled from '@emotion/styled';
import {FormControl, TextField, InputLabel, Select, MenuItem, Button} from '@mui/material';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {updateLeague, clearCurrenLeagueError} from 'modules/actions/leagues';
import {isLeagueRequested, getCurrentLeague, getCurrentLeagueError, getCurrentUserId} from 'modules/selectors';
import {useMediaQuery} from 'modules/utils';
import {LeaguePrivacy} from 'modules';
import {LeagueUsers} from 'pages/group/groupSettings/LeagueUsers';
import DeleteLeague from 'pages/group/groupSettings/updateGroup/DeleteLeague';
import {Exist} from 'components';
import {validateLeagueName} from 'modules/utils/validations';

const Container = styled.section`
	flex: 1 1 300px;
	padding: 16px 20px 20px;
	gap: 20px;
	background: #292f37;
	border-radius: 8px;
	margin-top: 16px;
	width: 100%;
	flex-shrink: 0;
	align-self: flex-start;
	position: sticky;
	top: 10px;

	@media (max-width: 1150px) {
		max-width: 100%;
		background: transparent;
		order: 1;
		padding-bottom: 0;
		flex: auto;
	}
`;

const Title = styled.div`
	font-weight: 700;
	font-size: 18px;
	line-height: 150%;
	margin-bottom: 16px;
`;

const Form = styled.form`
	margin: 20px 0;
	display: flex;
	flex-flow: column;
`;

const Users = styled.div`
	width: 100%;
	z-index: 2;
	@media (max-width: 1150px) {
		order: 2;
	}
`;

const Delete = styled.div`
	width: 100%;
	padding: 0 20px 20px;
	@media (max-width: 1150px) {
		order: 3;
	}
`;

const SubTitle = styled.div`
	font-style: normal;
	font-weight: 700;
	font-size: 16px;
	line-height: 130%;
	color: #ffffff;
	margin-bottom: 10px;
`;

const Description = styled.div`
	font-style: normal;
	font-weight: 400;
	font-size: 16px;
	line-height: 130%;
	display: flex;
	align-items: center;
	color: #ffffff;
	margin-bottom: 15px;

	&.capitalize {
		text-transform: capitalize;
	}
`;

interface IFormElement {
	groupName: HTMLInputElement;
	privacy: HTMLInputElement;
}

const useUpdateGroup = () => {
	const i18n = useTranslation();
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(`(max-width: 1150px)`);
	const isRequested = useSelector(isLeagueRequested);
	const currentLeague = useSelector(getCurrentLeague);
	const error = useSelector(getCurrentLeagueError);
	const userID = useSelector(getCurrentUserId);
	const isCommissioner = currentLeague?.userId === userID;
	const [privacyValue, setPrivacyValue] = useState(currentLeague?.privacy);
	const [isUpdateEnabled, setEnabledState] = useState(false);
	const [isDisabled, setDisabled] = useState(true);

	const clearError = () => {
		dispatch(clearCurrenLeagueError());
	};

	const validate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const errors = validateLeagueName(e.currentTarget);
		setDisabled(!!errors);
	};

	return {
		i18n,
		dispatch,
		isRequested,
		currentLeague,
		isMobile,
		isCommissioner,
		error,
		clearError,
		privacyValue,
		setPrivacyValue,
		setEnabledState,
		isUpdateEnabled,
		validate,
		isDisabled,
		setDisabled,
	};
};

const UpdateGroup: React.FC = () => {
	const {
		i18n,
		dispatch,
		error,
		clearError,
		currentLeague,
		privacyValue,
		setPrivacyValue,
		setEnabledState,
		isUpdateEnabled,
		isMobile,
		isDisabled,
		validate,
		setDisabled,
	} = useUpdateGroup();

	if (!currentLeague) {
		return null;
	}

	return (
		<>
			<Users>
				<LeagueUsers />
			</Users>

			<Container>
				<div>
					<Title>{i18n.t("update_league.form.section_title", "Settings")}</Title>
					<Form
						onSubmit={(evt: React.SyntheticEvent<HTMLFormElement>) => {
							evt.preventDefault();
							const {groupName, privacy} =
								evt.currentTarget as unknown as IFormElement;

							dispatch(
								updateLeague({
									leagueId: currentLeague.id,
									name: groupName.value,
									privacy: privacy.value as LeaguePrivacy,
								})
							);
							return false;
						}}
					>
						<Exist when={Boolean(currentLeague.description)}>
							<SubTitle>{i18n.t("about_league.description", "Description")}</SubTitle>
							<Description className={"capitalize"}>
								{currentLeague.description}
							</Description>
							<br />
						</Exist>

						<TextField
							id="league-name"
							label={i18n.t("update_league.form.name_label", "Group name")}
							name="groupName"
							variant="outlined"
							placeholder={i18n.t(
								"update_league.form.placeholder",
								"Enter Group Name Here"
							)}
							defaultValue={currentLeague?.name}
							onChange={(e) => {
								clearError();
								validate(e);
								setEnabledState(true);
							}}
							required={true}
							error={!!error}
							helperText={error ? i18n.t(error) : " "}
						/>

						<FormControl sx={{mb: "16px"}}>
							<InputLabel id="privacy-label">
								{i18n.t("update_league.form.privacy", "Privacy")}
							</InputLabel>
							<Select
								labelId="privacy-label"
								id="privacy"
								name="privacy"
								value={privacyValue}
								defaultValue={currentLeague.privacy}
								onChange={(event) => {
									setPrivacyValue(event.target.value as LeaguePrivacy);
									setEnabledState(true);
									setDisabled(false);
								}}
								label={i18n.t("update_league.form.privacy", "Privacy")}
								required={true}
							>
								<MenuItem value={LeaguePrivacy.PUBLIC}>
									{i18n.t("update_league.form.privacy_public", "Public")}
								</MenuItem>
								<MenuItem value={LeaguePrivacy.PRIVATE}>
									{i18n.t("update_league.form.privacy_private", "Private")}
								</MenuItem>
							</Select>
						</FormControl>

						<Button
							variant={"contained"}
							color={"secondary"}
							type="submit"
							disabled={!isUpdateEnabled || isDisabled}
						>
							{i18n.t("update.form.update_button", "Update League")}
						</Button>
					</Form>
				</div>

				<Exist when={!isMobile}>
					<DeleteLeague currentLeague={currentLeague} />
				</Exist>
			</Container>
			<Exist when={isMobile}>
				<Delete>
					<DeleteLeague currentLeague={currentLeague} />
				</Delete>
			</Exist>
		</>
	);
};

export default UpdateGroup;
